import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import Typography from "@mui/joy/Typography";
import Link from "@mui/joy/Link";
import { theme } from "styles/theme";
import { useQuery } from "@tanstack/react-query";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { useRecoilValue } from "recoil";
import { PATH } from "routes/paths";
import {
  PAYMENT_DOMAIN_V2,
  PAYMENT_TOSSPAYMENTS_PROMOTION_CONFIRM_ENDPOINT,
  paymentApi,
} from "../../../api/paymentApi";
import useAutoLoginV3 from "./useAutoLoginV3";
import useGtagConversion from "../../../hooks/useGtagConversion";

export default function TossPaymentsSuccessV3() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get("orderId");
  const paymentKey = searchParams.get("paymentKey");
  const payAmount = Number(searchParams.get("amount"));

  useAutoLoginV3();
  const user = useRecoilValue(userSimpleAtom);
  const trackGtagEvent = useGtagConversion();

  const { isLoading, isSuccess, isError } = useQuery({
    queryKey: [
      PAYMENT_DOMAIN_V2,
      PAYMENT_TOSSPAYMENTS_PROMOTION_CONFIRM_ENDPOINT,
      orderId,
      paymentKey,
      payAmount,
    ],
    enabled: Boolean(
      orderId &&
        paymentKey &&
        payAmount &&
        user?.trackId &&
        user?.userId &&
        user?.email
    ),
    queryFn: async () => {
      return await paymentApi.confirmTossPaymentsV3({
        orderId: orderId as string,
        paymentKey: paymentKey as string,
        payAmount: payAmount as number,
        version: "v3"
      });
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 0,
    onSuccess: (responseData) => {
      if (
        responseData?.result?.isConfirmed !== true &&
        responseData?.result?.failureMessage
      ) {
        alert(responseData?.result?.failureMessage);
        return;
      }

      // 이후 이벤트 전송
      trackCapiStandardEvent(
        "Purchase",
        {
          value: 395000,
          currency: "KRW",
          externalId: user?.userId,
          email: user?.email,
        },
        {
          eventId: orderId ?? undefined,
        },
        "v3"
      );

      // Google Ads 전환 이벤트 전송
      trackGtagEvent("Purchase", "v3", {
        transactionId: orderId ?? undefined,
        value: 395000,
        currency: "KRW",
      });
    },
    onError: () => {
      alert("결제가 실패했어요. 다시 시도해 주세요.");
    },
  });

  useEffect(() => {
    if (isSuccess) {
      navigate(PATH.v3Completed, { replace: true });
    }
  }, [isSuccess, navigate]);

  if (!orderId || !paymentKey || !payAmount) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>{"만료된 페이지"}</Typography>
          </Stack>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  if (isError) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <Typography variant={"H3"}>
              {"결제 처리 중 오류가 발생했습니다."}
            </Typography>
            <Typography variant={"H3"}>
              <Link
                href="mailto:support@orwellhealth.org"
                sx={{
                  textDecoration: "underline",
                  color: theme.vars.palette.Content.Weak,
                }}
              >
                support@orwellhealth.org
              </Link>
              {"로 문의해 주세요."}
            </Typography>
          </Stack>
        }
      />
    );
  }

  if (isSuccess) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  return null;
}
